// ─── Global design tokens ────────────────────────────────────────────────────
//
// THE source of design values for the whole frontend — colour, spacing, radius,
// motion, breakpoints, type. Locked by
// `Brand_Design/mystack-design-system-brief-Aug.html` (v1.1, August 2026).
//
// Promoted from `src/components/landing/landingTokens.js`, which was already
// ~90% the brief; that file is now a re-export shim so the landing surface did
// not have to churn. See `docs/design-system-compliance/DESIGN_DOC.md` D-1/D-2.
//
// Two rules that make this file the single source:
//   1. Never add a hex here that the brief does not lock. If the brief is
//      silent, the value is a legacy value — mark it as one (see `color` below).
//   2. Every value here is mirrored into `cssVariables` and emitted on `:root`
//      in `src/index.css`, so legacy `.css` files can reach tokens too.
//      `tokens.test.js` fails if the two ever disagree.
//
// Naming follows the BRIEF, not the old landing file. Where they disagree —
// the brief calls `#9FD9BE` "Soft Accent" and reserves "Mint" for `#E4EFE8` —
// the brief wins here and the shim maps the old landing name.
// ─────────────────────────────────────────────────────────────────────────────

// px → rem helper (root font-size 16px). Frontend rule: measurements in rem.
export const r = (px) => `${px / 16}rem`;

// ─── Colour ──────────────────────────────────────────────────────────────────
export const color = {
  // Brand palette — brief §3. Two greens do the work.
  evergreen:     '#185002', // Deep Evergreen — structure: nav, footer, dark surfaces
  // Hover for deep-evergreen FILLS. Not brief-locked — the brief defines no
  // deep hover value, so per rule 1 above this is an unlocked value, added for
  // the affiliate Buy CTA exception (STACK-510, ruled 2026-08-11 by Tanu; see
  // docs/design-language/DESIGN_TOKENS.md). Deliberately a *lightening* of
  // `evergreen`, matching how `accentHover` lightens `accent` — the opposite
  // of the darker shade MUI derives for `primary.dark`. White on it is 7.6:1,
  // past AA at body size.
  evergreenHover: '#1d6103',
  accent:        '#0A9C5F', // Signal Accent — THE action colour: CTAs, links, relevance fill
  accentInk:     '#04231a', // text on accent fills (white fails AA at body size)
  accentHover:   '#09B870',
  // **The accent-text value, and brief-locked** (v1.1 §3, "Signal Accent —
  // text"). Ruled by Tanu 2026-08-29, and the lock is the point of the ruling:
  // `accent` is the action colour for *fills*, where `accentInk` carries the
  // contrast, but as text on `paper` it measures 3.44:1 and fails AA. This is
  // the same green deep enough to read — 5.24:1 on `paper` — so green text
  // across the product is one value rather than a per-surface judgement.
  //
  // It sat here unmarked until STACK-527, which is what rule 1 above exists to
  // catch: unlike `evergreenHover` it was neither locked by the brief nor
  // labelled a legacy value, so a reader had no way to tell whether it could be
  // changed. The brief's own "links: signal accent (#0A9C5F)" line and its
  // `4.85:1 ✓ AA` badge measure ink-on-accent — the fill case — and never
  // measure accent as text on a light ground. The brief now carries this value
  // beside it; do not re-derive either one.
  linkHover:     '#097a4a',
  softAccent:    '#9FD9BE', // mark lines + dim nodes ON DARK SURFACES ONLY
  heroMid:       '#0a8a58', // hero gradient midpoint
  heroDark:      '#0b2800', // hero gradient endpoint ONLY — never a standalone surface

  // Neutral palette — brief §3.
  ink:           '#0C1F17', // body text, headings, strength-bar fill
  inkSoft:       '#5C6B64', // secondary text, descriptions, labels  (brief: Muted)
  inkMuted:      '#8A988F', // tertiary text, overline labels        (brief: Muted 2)
  paper:         '#FBFCFB', // primary background
  surface:       '#F4F7F5', // cards, alt rows, section backgrounds
  mint:          '#E4EFE8', // tinted section ground — the third surface (v1.1 D8)
  mintSoft:      '#EDF4EF', // nested panels inside a mint section
  line:          '#DDE5E0', // all borders, rules, dividers
  lineSoft:      '#EAF0EC', // lighter hairline
  // Handoff-locked — see the risk-chip block below for why rule 1 permits
  // these. Body copy one step lighter than `ink`; text on `evergreen` surfaces.
  textBody:      '#33403A',
  textOnDark:    '#EAF3EE',

  // Evidence display — brief §5.
  ringTrack:     '#E9EFEB', // unfilled track of the relevance ring
  strengthFill:  '#1C2C25', // study-strength segments, lit (graphite — never green)
  strengthEmpty: '#CBD6D0', // study-strength segments, unlit

  // Semantic — INTERACTION RISK ONLY. Never decorative. Brief §3.
  riskHigh:      '#D6453C',
  riskMedium:    '#DC8209',
  riskLow:       '#0A9C5F', // the signal accent doubles as "no concern"

  // ── Risk-chip and confidence-badge surfaces ───────────────────────────────
  // Rule 1 at the top of this file says never add a hex the brief does not
  // lock. These are locked by `Brand_Design/myStack - Stack States Handoff.html`
  // — a brief artifact of the same system rather than a new source — so the
  // rule is noted as widened here rather than silently broken (STACK-480
  // ruling, 2026-08-12).
  //
  // Only the HIGH-risk chip needs new values. Medium and low already ship at
  // exactly the handoff's values under legacy landing names — medium is
  // `cautionBg`/`cautionBorder`/`cautionInk`, low is `posBg`/`posBorder`/
  // `posInk` — so those are consumed rather than duplicated. Whether that pair
  // should be renamed into a risk vocabulary is an open proposal (STACK-480),
  // deliberately not decided here: a rename touches the landing surface.
  riskHighBg:     '#FBEBEA', // → --risk-hi-bg, high-risk chip fill
  riskHighBorder: '#F0C8C4', // → --risk-hi-bd, high-risk chip border
  riskHighInk:    '#A82E26', // → --risk-hi-fg, high-risk chip text

  // Confidence badges: high is text-only, medium carries a fill.
  confHighInk:    '#067A57', // → --conf-hi-fg
  confMediumBg:   '#FFF4E3', // → --conf-md-bg
  confMediumInk:  '#9A5A06', // → --conf-md-fg

  // Semantic — DESTRUCTIVE ACTIONS ONLY. Ruling in DESIGN_DOC §10 (2026-08-08):
  //
  //   Red = this action destroys something.  Amber = this may harm you.
  //
  // Two different axes: one is about the action you are taking, the other about
  // the state of your health data. Red on a delete control is a near-universal
  // affordance and users rely on it to know an action is irreversible, so it is
  // kept — but as its own token, not as a second meaning for `riskHigh`.
  //
  // Deliberately NOT the same value as `riskHigh`: the design-system guard
  // exempts this exact hex from its `risk-color` rule, and sharing a value
  // would silently exempt the interaction-risk red everywhere too. The brief
  // locks no destructive red (its only reds are the risk-high badge family), so
  // this is the legacy value already shipping on delete controls — see rule 1
  // at the top of this file.
  //
  // Reach for it ONLY where the action destroys something: delete account,
  // remove medication, discard unsaved edits. Cancelling out of an editor or
  // navigating to a settings page is not destructive — use neutral or accent.
  destructive:   '#d32f2f',

  // ── Legacy: in use on the landing surface, not locked by the brief ─────────
  // Kept so the landing renders unchanged. Do not reach for these on new
  // surfaces — use the brief-locked values above.
  borderSoft:    '#E7ECE9',
  posBg:         '#E5F6EE',
  posInk:        '#0a6b41',
  posBorder:     '#bfe6d2',
  cautionBg:     '#FBF1E2',
  cautionBorder: '#f0d6a8',
  cautionInk:    '#945406',
  navLink:       '#CFE3D8',
  heroLead:      '#C9DBD2',
};

// ─── Spacing — 8-base scale, brief §7 ────────────────────────────────────────
// Emitted in rem (repo rule) from the brief's px values: 4 8 16 24 32 48 64 96.
export const spacePx = { s1: 4, s2: 8, s3: 16, s4: 24, s5: 32, s6: 48, s7: 64, s8: 96 };
export const space = {
  s1: r(spacePx.s1), // micro — icon gaps, fine offsets
  s2: r(spacePx.s2), // compact — inline element gaps
  s3: r(spacePx.s3), // small — card internal padding
  s4: r(spacePx.s4), // medium — component gaps
  s5: r(spacePx.s5), // large — section gaps (mobile)
  s6: r(spacePx.s6), // xlarge — section padding
  s7: r(spacePx.s7), // 2xlarge — section spacing (desktop)
  s8: r(spacePx.s8), // 3xlarge — hero padding
};

// ─── Radius — the brief's three, plus the handoff's pill ─────────────────────
// `radiusPx` exists because MUI's `shape.borderRadius` takes a number.
//
// `pill` is the handoff's `--r-pill`, ruled at `999px` (STACK-480). It is
// carried through `r()` like every other measurement rather than kept in px:
// the repo rule is rem everywhere (UI/react/stack/CLAUDE.md rule 1), and the
// design-system guard's `px-literal` rule scans `src/index.css`, where a raw
// `999px` would be a new violation and fail `build-staging`. The ruling locks
// the value; the repo locks the unit, and `r(999)` satisfies both. Any radius
// past half the element's height renders the same pill.
export const radiusPx = { sm: 8, md: 12, lg: 16, pill: 999 };
export const radius = {
  sm: r(radiusPx.sm),
  md: r(radiusPx.md),
  lg: r(radiusPx.lg),
  pill: r(radiusPx.pill),
};

// ─── Motion — brief §8 ───────────────────────────────────────────────────────
// Product-wide motion language. The landing's scroll-reveal choreography is a
// separate, longer language and lives in `reveal` below.
export const motion = {
  micro: '150ms', // button press, toggle, checkbox
  ui:    '250ms', // panel open/close, modal, tab switch
  page:  '300ms', // route changes, screen-level transitions
  // Synthesis-node loop during stack generation and the interaction checker's
  // check ONLY (v5.1, STACK-587).
  loop:  '1.6s',
  ease:  'ease-in-out',
};

// ─── Elevation ───────────────────────────────────────────────────────────────
//
// Two shadows, and only two. The system's written policy is that hairlines
// separate cards — no shadow on any inline card or list. These exist for
// FLOATING elements only: `pop` for the undo toast, `modal` for the delete
// dialog (dashboard handoff §7 and §10).
//
// Transcribed verbatim from the `:root` block of
// `Brand_Design/myStack Dashboard - Engineering Handoff.html`, which agrees
// character-for-character with the other two handoffs. `rgba(12, 31, 23)` is
// `#0C1F17` — the system's own `ink` — so these tint rather than grey.
//
// The px here are deliberate and are the one place this module states a
// measurement in px rather than through `r()`. Unlike `--r-pill`, where
// `r(999)` renders identically to `999px` and so satisfied both the ruling and
// the repo's rem rule, converting a shadow's offsets and blur would be
// *re-deriving* a design-locked value — which STACK-515 forbids explicitly.
// The design-system guard counts these as `px-literal` findings here and in
// `src/index.css`; that cost is accepted in the baseline rather than paid by
// altering the values. See the PR's Decisions table.
export const shadow = {
  pop:   '0 8px 28px rgba(12, 31, 23, 0.14), 0 2px 6px rgba(12, 31, 23, 0.08)',
  modal: '0 24px 60px rgba(12, 31, 23, 0.22), 0 4px 12px rgba(12, 31, 23, 0.10)',
};

// ─── Type ────────────────────────────────────────────────────────────────────
export const FONT_FAMILY =
  "'Archivo', ui-sans-serif, system-ui, -apple-system, sans-serif";

// The one documented exception to "Archivo everywhere" (DESIGN_DOC §10,
// 2026-08-06): genuine code surfaces — the agent activity log and blog code
// blocks — keep a mono stack, because proportional type in a code block is
// materially worse to read. The brief mandates one font but names no mono
// face, which reads as an omission rather than a prohibition. Courier New was
// retired with the other eight faces; Geist Mono leads here instead.
export const FONT_MONO =
  "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// Tabular figures. Proportional digits make a column of doses or scores look
// ragged because `1` is narrower than `8`; `tnum` locks every digit to the same
// advance width so they line up. Spread into an `sx`, or reach it through the
// theme's `data` typography variant.
export const tabularNums = {
  fontVariantNumeric: 'tabular-nums',
  fontFeatureSettings: '"tnum" 1',
};

// ─── Layout ──────────────────────────────────────────────────────────────────
export const MAXW = '67.5rem';                          // 1080px content max-width
export const PAD_X = 'clamp(1.125rem, 5vw, 1.75rem)';   // 18–28px
export const SECTION_Y = 'clamp(3.5rem, 8vw, 6.25rem)'; // 56–100px

// Shared section container sx (centred, max-width, horizontal padding).
export const containerSx = {
  maxWidth: MAXW,
  mx: 'auto',
  px: PAD_X,
};

// ─── Breakpoints — mobile-first, min-width, brief §7 ─────────────────────────
// Used as sx media-query keys (not MUI's default 600/900/1200 scale).
export const BP = {
  phoneLg: '@media (min-width:480px)',
  tablet:  '@media (min-width:768px)',
  desktop: '@media (min-width:1024px)',
  wide:    '@media (min-width:1280px)',
};

// ─── Scroll-reveal choreography (landing) ────────────────────────────────────
// One signature language for all scroll reveals: one ease-out glides content
// into place; a single rise distance, duration, and stagger step keep every
// section feeling like the same system.
export const reveal = {
  // easeOutQuint: same glide family as easeOutExpo but spreads the travel
  // across more of the duration — perceived slower and smoother (expo dumped
  // ~80% of the movement into the first fifth, making everything read fast).
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
  y: 24,           // rise distance (px) — section-level reveals
  ySm: 12,         // rise distance (px) — detail elements inside cards
  duration: 900,   // ms — section reveals, sweeps, count-ups
  durationSm: 600, // ms — detail strokes (bars, checkmark draws)
  // 120ms crosses the perceptual threshold (~100ms) at which staggered
  // siblings stop reading as simultaneous — cascades become legible sequences.
  stagger: 120,
  // Reveal trigger: the element must rise ~25% up from the viewport bottom
  // before animating, so motion plays where the eye is looking rather than at
  // the screen edge.
  trigger: '0px 0px -25% 0px',
};

// JS twin of `reveal.ease` for requestAnimationFrame animations (ring sweep,
// count-ups) so scripted motion matches the CSS transitions exactly.
export const easeOut = (t) => (t >= 1 ? 1 : 1 - Math.pow(1 - t, 5)); // easeOutQuint

// ─── CSS custom properties ───────────────────────────────────────────────────
//
// The contract between this module and every `.css` file in the repo. The
// literal `:root` block in `src/index.css` must match this object exactly —
// `tokens.test.js` asserts both directions, so drift cannot merge.
//
// Names mirror the brief's own `:root` block wherever the brief defines one,
// so a reader can diff the brief against the code line by line.
export const cssVariables = {
  // Brand
  '--deep':          color.evergreen,
  '--deep-hover':    color.evergreenHover,
  '--accent':        color.accent,
  '--accent-ink':    color.accentInk,
  '--accent-hover':  color.accentHover,
  '--link-hover':    color.linkHover,
  '--soft-accent':   color.softAccent,
  '--hero-mid':      color.heroMid,
  '--hero-dark':     color.heroDark,
  // Neutral
  '--ink':           color.ink,
  '--text-body':     color.textBody,
  '--muted':         color.inkSoft,
  '--muted2':        color.inkMuted,
  '--text-on-dark':  color.textOnDark,
  '--paper':         color.paper,
  '--surface':       color.surface,
  '--mint':          color.mint,
  '--mint2':         color.mintSoft,
  '--line':          color.line,
  '--lsoft':         color.lineSoft,
  '--border-soft':   color.borderSoft,
  // Evidence
  '--surf2':         color.ringTrack,
  '--str-fill':      color.strengthFill,
  '--str-mt':        color.strengthEmpty,
  // Risk
  '--risk-hi':       color.riskHigh,
  '--risk-md':       color.riskMedium,
  '--risk-lo':       color.riskLow,
  // Risk-chip surfaces (high only — medium and low reuse --caution-* / --pos-*)
  '--risk-hi-bg':    color.riskHighBg,
  '--risk-hi-bd':    color.riskHighBorder,
  '--risk-hi-fg':    color.riskHighInk,
  // Confidence badges
  '--conf-hi-fg':    color.confHighInk,
  '--conf-md-bg':    color.confMediumBg,
  '--conf-md-fg':    color.confMediumInk,
  // Destructive actions — a different axis from risk. See `color.destructive`.
  '--destructive':   color.destructive,
  // Legacy status surfaces (landing)
  '--pos-bg':        color.posBg,
  '--pos-ink':       color.posInk,
  '--pos-border':    color.posBorder,
  '--caution-bg':    color.cautionBg,
  '--caution-border': color.cautionBorder,
  '--caution-ink':   color.cautionInk,
  '--nav-link':      color.navLink,
  '--hero-lead':     color.heroLead,
  // Spacing
  '--s1':            space.s1,
  '--s2':            space.s2,
  '--s3':            space.s3,
  '--s4':            space.s4,
  '--s5':            space.s5,
  '--s6':            space.s6,
  '--s7':            space.s7,
  '--s8':            space.s8,
  // Radius
  '--r-sm':          radius.sm,
  '--r-md':          radius.md,
  '--r-lg':          radius.lg,
  '--r-pill':        radius.pill,
  // Motion
  '--t-micro':       motion.micro,
  '--t-ui':          motion.ui,
  '--t-page':        motion.page,
  '--t-loop':        motion.loop,
  '--ease':          motion.ease,
  // Elevation — floating elements only. See `shadow` above.
  '--shadow-pop':    shadow.pop,
  '--shadow-modal':  shadow.modal,
  // Layout + type
  '--mw':            MAXW,
  '--font-sans':     FONT_FAMILY,
  '--font-mono':     FONT_MONO,
};

// Renders `cssVariables` as the body of a `:root` rule. Used by the test to
// compare against `src/index.css`; also the thing to paste when regenerating
// that block by hand.
export const cssVariableBlock = () =>
  Object.entries(cssVariables)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');
